import { useState, useCallback, useEffect } from "react";
import { cn } from "utils/helpers";
import SkeletonBase from "newui/components/Skeleton";
import { usePanelMemberStatus } from "../../../../hooks/usePanelScoringQueries";
import { useInitialEvaluationContext } from "../../../../hooks/InitialEvaluationContext";
import { PanelMemberList } from "./PanelMemberList";
import { LeadScoringToggle } from "./LeadScoringToggle";
import { LeadScoringSection } from "./LeadScoringSection";
import { MemberScoreAccordion } from "./MemberScoreAccordion";
import { AverageScoringTable } from "./AverageScoringTable";
import { CompanyUserReadOnlyView } from "./CompanyUserReadOnlyView";
import { EvaluationClarificationsTable } from "./clarifications/EvaluationClarificationsTable";
import type { Rfx } from "newui/features/Rfx/types/rfx.types";

interface PanelEvaluationViewProps {
	rfxData: Rfx;
	evalId: number;
	isRfxLead: boolean;
	onCompletionChange?: (isComplete: boolean) => void;
}

type TabId = "my-scoring" | "panel-members" | "my-clarifications";

interface TabConfig {
	id: TabId;
	label: string;
	testId: string;
}

export const PanelEvaluationView: React.FC<PanelEvaluationViewProps> = ({
	rfxData,
	evalId,
	isRfxLead,
	onCompletionChange,
}) => {
	const rfxId = rfxData.id;
	const { data: members, isLoading, refetch: refetchMembers } = usePanelMemberStatus(rfxId, evalId);
	const { suppliers } = useInitialEvaluationContext();
	const [activeTab, setActiveTab] = useState<TabId>("my-scoring");
	const [isScoringOverride, setIsScoringOverride] = useState<boolean | null>(null);

	const handleLeadSubmitted = useCallback(() => {
		refetchMembers();
	}, [refetchMembers]);

	const handleScoringToggle = useCallback((isScoring: boolean) => {
		setIsScoringOverride(isScoring);
		if (!isScoring) {
			setActiveTab("panel-members");
		}
	}, []);

	const activeMembers = (members ?? []).filter((m) => !m.is_removed);
	const allSubmitted = activeMembers.length > 0 &&
		activeMembers.every((m) => m.status === "submitted" || m.status === "declined" || m.status === "revoked");

	useEffect(() => {
		onCompletionChange?.(allSubmitted);
	}, [allSubmitted, onCompletionChange]);

	if (isLoading) {
		return (
			<div className="space-y-4" data-testid="panel-evaluation-loading">
				{[1, 2, 3].map((i) => (
					<SkeletonBase key={i} startColor="lightgray" width="100%" height="56px" className="!rounded-lg" />
				))}
			</div>
		);
	}

	// Non-lead, non-panel users see a read-only view
	if (!isRfxLead) {
		return (
			<CompanyUserReadOnlyView
				rfxLeadName={rfxData.rfxLead?.fullName ?? "Unknown"}
				rfxLeadEmail={rfxData.rfxLead?.email ?? ""}
				rfxLeadPhone={rfxData.rfxLead?.phoneNumber}
			/>
		);
	}

	const isLeadSubmitted = activeMembers.some(
		(m) => m.role === "chair" && m.status === "submitted"
	);
	const isLeadCurrentlyScoring = isScoringOverride ?? activeMembers.some(
		(m) => m.role === "chair" && !m.is_removed
	);
	const anyMemberSubmitted = activeMembers.some(
		(m) => m.role !== "chair" && m.status === "submitted"
	);
	const leadScoreVisibility = (members ?? [])[0]?.lead_score_visibility ?? "always";

	// Build tabs: My Scoring only when lead is actively scoring and hasn't submitted
	const showMyScoring = isLeadCurrentlyScoring && !isLeadSubmitted;

	const tabs: TabConfig[] = [
		...(showMyScoring ? [{ id: "my-scoring" as TabId, label: "My Scoring", testId: "tab-my-scoring" }] : []),
		{ id: "my-clarifications", label: "My Clarifications", testId: "tab-my-clarifications" },
		{ id: "panel-members", label: "Panel Members", testId: "tab-panel-members" },
	];

	// If active tab is no longer available, switch to first available
	const effectiveTab = tabs.some((t) => t.id === activeTab) ? activeTab : tabs[0].id;

	return (
		<div className="space-y-6" data-testid="panel-evaluation-view">
			<LeadScoringToggle
				rfxId={rfxId}
				evalId={evalId}
				initialIsScoring={isLeadCurrentlyScoring}
				leadScoreVisibility={leadScoreVisibility}
				anyMemberSubmitted={anyMemberSubmitted}
				onToggle={handleScoringToggle}
			/>

			<div className="flex border-b border-gray-200" data-testid="panel-tabs" role="tablist">
				{tabs.map((tab) => (
					<button
						key={tab.id}
						type="button"
						role="tab"
						aria-selected={effectiveTab === tab.id}
						aria-controls={`tab-panel-${tab.id}`}
						onClick={() => setActiveTab(tab.id)}
						className={cn(
							"px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px",
							effectiveTab === tab.id
								? "border-[#0031DD] text-[#0031DD]"
								: "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
						)}
						data-testid={tab.testId}
					>
						{tab.label}
					</button>
				))}
			</div>

			{showMyScoring && (
				<div
					id="tab-panel-my-scoring"
					role="tabpanel"
					className={effectiveTab === "my-scoring" ? "space-y-6" : "hidden"}
				>
					<LeadScoringSection
						rfxId={rfxId}
						evalId={evalId}
						isLeadScoring={isLeadCurrentlyScoring}
						isLeadSubmitted={isLeadSubmitted}
						onSubmitted={handleLeadSubmitted}
					/>
				</div>
			)}

			<div
				id="tab-panel-panel-members"
				role="tabpanel"
				className={effectiveTab === "panel-members" ? "space-y-6" : "hidden"}
			>
				<PanelMemberList
					rfxId={rfxId}
					evalId={evalId}
				/>

				<MemberScoreAccordion
					rfxId={rfxId}
					evalId={evalId}
					members={activeMembers}
					isLeadSubmitted={isLeadSubmitted}
					leadScoreVisibility={leadScoreVisibility}
					isLeadScoring={isLeadCurrentlyScoring}
					onEnterScores={showMyScoring ? () => setActiveTab("my-scoring") : undefined}
				/>

				<AverageScoringTable
					rfxId={rfxId}
					evalId={evalId}
					allSubmitted={allSubmitted}
				/>
			</div>

			<div
				id="tab-panel-my-clarifications"
				role="tabpanel"
				className={effectiveTab === "my-clarifications" ? "space-y-6" : "hidden"}
			>
				{suppliers.length > 0 && (
					<EvaluationClarificationsTable
						rfxId={rfxId}
						evalId={evalId}
						suppliers={suppliers.map((s) => ({
							inviteId: s.inviteId,
							supplierName: s.supplierName,
						}))}
					/>
				)}
			</div>
		</div>
	);
};
